import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Layout from "components/Layout";
import heroHoffmannVideo from "assets/videos/hoffmann_hero.mp4";
import dhMassageVideo from "assets/videos/Hoffmann_Video_mit_Sound.mp4";
import dhVideoThumbnail from "assets/images/dh_video_thumbnail.jpg";
import spaCosmeticImg from "assets/images/spa_cosmetic_img.png";
import fitnessSportImg from "assets/images/fitness_sport_img.png";
import unternehmerImg from "assets/images/unternehmer_img.png";
import suraMassiert1 from "assets/images/sura_massiert_1.png";
import OfferDetailsModal from "components/OfferDetailsModal";
import { useMonthlyAction } from "utils/monthlyAction";

const CUSTOMER_PRIVATE = "private";
const CUSTOMER_BUSINESS = "business";

const CUSTOMER_TYPES = [
  { id: CUSTOMER_PRIVATE, label: "Privat" },
  { id: CUSTOMER_BUSINESS, label: "Geschäftlich" },
];

const bookingHrefForOffer = (offerId, customerType, monthlyAction = false) => {
  const params = new URLSearchParams({ option: offerId });
  if (customerType === CUSTOMER_BUSINESS) {
    params.set("audience", CUSTOMER_BUSINESS);
  }
  if (monthlyAction && offerId === "wakeup-plus-business") {
    params.set("monthlyAction", "1");
  }
  return `/buchen?${params.toString()}`;
};

const PRIVATE_OFFER_CARDS = [
  {
    id: "wakeup",
    detailsId: "wakeup",
    title: "Wake-Up",
    subtitle: "Schnelle Energie für deinen Tag",
    description:
      "Ein kurzer, wohltuender Impuls für Körper und Kopf – ideal für zwischendurch.",
    price: "ab 5 €",
    priceNote: "p. P.",
  },
  {
    id: "classic",
    detailsId: "classic",
    title: "Classic",
    subtitle: "Ganzkörper-Energie für deine Woche",
    description:
      "Entspannung, Lockerung und Zeit für dich – so lange, wie dein Alltag es zulässt.",
    price: "ab 29 €",
    priceNote: "/ 10 Min.",
    featured: true,
    spotlight: "Meist gebucht",
  },
  {
    id: "premium",
    detailsId: "intense",
    title: "Intense",
    subtitle: "Neue Energie für dein Leben",
    description:
      "Deine persönliche Auszeit mit individuell abgestimmter Massage, Bewegung und Entspannung.",
    price: "ab 70 €",
    priceNote: "/ 30 Min.",
  },
];

const BUSINESS_OFFER_CARDS = [
  {
    id: "wakeup-plus-business",
    detailsId: "wakeup-plus-business",
    title: "Wake-Up+",
    subtitle: "Für Neukunden & Veranstaltungen",
    description:
      "Für fitte, produktive Teams. Wake-Up Massage ab 1 Person, maximal bis zu 30 Min.",
    price: "ab 79 €",
    priceNote: "netto zzgl. MwSt.",
    spotlight: "Meist gebucht",
  },
  {
    id: "classic-plus-business",
    detailsId: "classic-plus-business",
    title: "Classic+",
    subtitle: "Löst Ganzkörperverspannungen",
    description:
      "10 Minuten Classic Massage: im Studio oder bei Ihnen vor Ort.",
    price: "29 €",
    priceNote: "netto zzgl. MwSt.",
  },
  {
    id: "intense-plus-business",
    detailsId: "intense-plus-business",
    title: "Intense+",
    subtitle: "Personal Training für dein Team",
    description:
      "60 Minuten individuelle Begleitung: im Studio oder bei Ihnen vor Ort.",
    price: "125 €",
    priceNote: "netto pro Stunde zzgl. MwSt.",
  },
];

const PRIVATE_FAQS = [
  {
    question: "Wie läuft mein Termin ab?",
    answer:
      "Du wählst online dein gewünschtes Angebot und deinen Termin aus. Vor Beginn sprechen wir kurz darüber, was dir heute guttut und welche Bereiche besonders berücksichtigt oder ausgelassen werden sollen. Anschließend genießt du deine persönliche Auszeit.",
  },
  {
    question: "Muss ich mich umziehen?",
    answer:
      "Nein. Für keine unserer Massagen musst du dich nicht umziehen. Komm einfach in bequemer Alltagskleidung - wir machen es dir so unkompliziert wie möglich.",
  },
  {
    question: "Wie lange dauert eine Massage?",
    answer:
      "Das hängt vom gewählten Angebot ab: Wake-Up ist die kurze Aktivierung für zwischendurch, Classic ist ab 10 Minuten flexibel buchbar und Intense bietet dir bis zu 60 Minuten persönliche 1:1-Auszeit. Die verfügbaren Zeitfenster siehst du bei der Terminbuchung.",
  },
  {
    question: "Welches Angebot passt zu mir?",
    answer:
      "Wake-Up passt, wenn du einen kurzen Energie-Impuls möchtest. Classic ist richtig, wenn du dir gezielt Zeit für Entspannung und Lockerung nehmen willst. Intense eignet sich für eine umfassende, individuell abgestimmte Auszeit mit Massage, Bewegung, Stretching und Entspannung.",
  },
  {
    question: "Welche Körperbereiche können berücksichtigt werden?",
    answer:
      "Je nach Anwendung können wir den Fokus zum Beispiel auf Nacken, Schultern, Rücken oder Beine legen. Teile uns vor Beginn einfach mit, welche Bereiche sich heute besonders beansprucht anfühlen oder ausgelassen werden sollen.",
  },
  {
    question: "Ich habe Beschwerden oder eine Verletzung - kann ich buchen?",
    answer:
      "Bitte informiere uns vor deinem Termin über aktuelle Beschwerden, Verletzungen, empfindliche Körperstellen oder gesundheitliche Besonderheiten. Bei akuten Beschwerden, offenen Wunden, Entzündungen, Fieber oder Unsicherheit empfehlen wir, vorab ärztlichen Rat einzuholen. Unsere Anwendungen sind Wellness-Angebote und ersetzen keine ärztliche Diagnose oder medizinische Behandlung.",
  },
  {
    question: "Kann ich während der Schwangerschaft buchen?",
    answer:
      "Nein. Während einer Schwangerschaft führen wir aus Sicherheitsgründen keine Massageanwendungen durch.",
  },
  {
    question: "Kann ich nach einer Operation einen Termin buchen?",
    answer:
      "Nein. Anwendungen nach Operationen führen wir aus Sicherheitsgründen nicht durch. Bitte plane erst dann einen Termin, wenn du vollständig genesen bist und keine postoperative Behandlung mehr besteht.",
  },
  {
    question: "Was kostet eine Massage?",
    answer:
      "Die Preise richten sich nach dem gewählten Angebot: Wake-Up ab 5 € pro Person, Classic ab 29 € pro Person und Intense ab 70 € pro Person. Den finalen Preis und verfügbare Optionen siehst du vor Abschluss deiner Buchung.",
  },
  {
    question: "Kann ich meinen Termin verschieben oder absagen?",
    answer:
      "Ja. Bitte nutze den Link in deiner Buchungsbestätigung oder storniere direkt auf unserer Website unter „Meine Termine“. Eine Stornierung ist bis 24 Stunden vor dem Termin möglich; danach gibt es keine Rückerstattung.",
  },
  {
    question: "Wo findet die Massage statt?",
    answer:
      "Dr. Hoffmann Massagen ist in Hamburg zu Hause, genau im Cremon 11, 20457 Hamburg. Weitere Standorte von Lizenznehmern kannst du bei der Buchung sehen.",
  },
  {
    question: "Ersetzt die Massage eine medizinische Behandlung?",
    answer:
      "Nein. Unsere Massagen dienen dem Wohlbefinden, der Entspannung und einer bewussten Auszeit im Alltag. Sie ersetzen keine ärztliche Untersuchung, Diagnose, Therapie oder physiotherapeutische Behandlung.",
  },
];

const HomePage = ({ initialCustomerType = CUSTOMER_PRIVATE }) => {
  const monthlyAction = useMonthlyAction();
  const location = useLocation();
  const [customerType, setCustomerType] = useState(initialCustomerType);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [openOfferDetails, setOpenOfferDetails] = useState(null);
  const [isMassageVideoPlaying, setIsMassageVideoPlaying] = useState(false);
  const faqItemRefs = useRef([]);
  const audienceSwitchRef = useRef(null);
  const heroVideoRef = useRef(null);
  const massageVideoRef = useRef(null);
  const offerCards =
    customerType === CUSTOMER_BUSINESS ? BUSINESS_OFFER_CARDS : PRIVATE_OFFER_CARDS;
  const offersBookingHref =
    customerType === CUSTOMER_BUSINESS ? "/buchen?audience=business" : "/buchen";

  const handleMassageVideoPlay = () => {
    massageVideoRef.current?.play();
  };

  const handleHeroScrollCue = () => {
    const target = audienceSwitchRef.current;
    if (!target) return;

    const targetRect = target.getBoundingClientRect();
    const targetTop = targetRect.top + window.scrollY;
    const centeredTop =
      targetTop - (window.innerHeight - targetRect.height) / 2;

    window.scrollTo({
      top: Math.max(centeredTop, 0),
      behavior: "smooth",
    });
  };

  const handleFaqToggle = (index) => {
    setOpenFaqIndex((currentIndex) => (currentIndex === index ? null : index));
    window.requestAnimationFrame(() => {
      faqItemRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  useEffect(() => {
    const items = document.querySelectorAll(".fade-in");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.16 }
    );
    items.forEach((it) => observer.observe(it));
    return () => observer.disconnect();
  }, [customerType]);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return undefined;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const playHeroVideo = () => {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    };

    const playWhenVisible = () => {
      if (document.visibilityState === "visible") playHeroVideo();
    };

    playHeroVideo();
    video.addEventListener("loadeddata", playHeroVideo);
    video.addEventListener("canplay", playHeroVideo);
    window.addEventListener("pageshow", playHeroVideo);
    document.addEventListener("visibilitychange", playWhenVisible);

    return () => {
      video.removeEventListener("loadeddata", playHeroVideo);
      video.removeEventListener("canplay", playHeroVideo);
      window.removeEventListener("pageshow", playHeroVideo);
      document.removeEventListener("visibilitychange", playWhenVisible);
    };
  }, []);

  useEffect(() => {
    if (!location.hash) return undefined;

    const hashId = decodeURIComponent(location.hash.slice(1));
    const targetId = hashId === "lizenz" ? "lizenzmodell" : hashId;
    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const navHeight = document.querySelector("nav")?.offsetHeight || 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: Math.max(targetTop - navHeight - 14, 0),
        behavior: "smooth",
      });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [location.hash]);

  return (
    <Layout>
      {/* ─── 1. Hero ─── */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-content">
            <h1>
              <span className="hero-mobile-hide">Die </span>D<span className="hero-hoffmann-smallcaps">r</span>. H<span className="hero-hoffmann-smallcaps">offmann</span> Massage
            </h1>
            <p className="hero-lead">
              {customerType === CUSTOMER_BUSINESS
                ? "Schnell. Smart. Skalierbar."
                : "Immer und überall."}
            </p>
            <div className="hero-actions">
              <Link to="/buchen" className="btn-primary">
                Jetzt Termin buchen
              </Link>
              {customerType !== CUSTOMER_BUSINESS && (
                <Link to="/shop" className="btn-ghost hero-voucher-cta">
                  Gutschein kaufen
                </Link>
              )}
            </div>
          </div>

          <div className="hero-visual hero-video-stage" aria-label="Dr. Hoffmann Massage Impressionen">
            <video
              ref={heroVideoRef}
              className="hero-video"
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              disablePictureInPicture
              preload="auto"
              aria-label="Dr. Hoffmann Massage Anwendung in Hamburg"
            >
              <source src={heroHoffmannVideo} type="video/mp4" />
            </video>
          </div>
          <button
            type="button"
            className="hero-mobile-scroll-cue"
            aria-label="Zu Privat oder Geschäftlich scrollen"
            onClick={handleHeroScrollCue}
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* ─── 2. Idea ─── */}
      <section className="home-audience-section" aria-labelledby="home-audience-title">
        <div className="home-audience-inner">
          <h2 id="home-audience-title">Bist du Privat- oder Geschäftskunde?</h2>
          <div
            ref={audienceSwitchRef}
            className="home-audience-switch"
            role="tablist"
            aria-label="Kundentyp auswählen"
          >
            {CUSTOMER_TYPES.map((type) => {
              const isActive = customerType === type.id;
              return (
                <button
                  key={type.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`home-audience-tab ${isActive ? "is-active" : ""}`}
                  onClick={() => setCustomerType(type.id)}
                >
                  {type.label}
                </button>
              );
            })}
          </div>
          <noscript>
            <p>
              <a href={customerType === CUSTOMER_PRIVATE ? "/unternehmen" : "/"}>
                {customerType === CUSTOMER_PRIVATE
                  ? "Angebote für Geschäftskunden ansehen"
                  : "Angebote für Privatkunden ansehen"}
              </a>
            </p>
          </noscript>
        </div>
      </section>

      <section className="home-offers-section" aria-labelledby="home-offers-title">
        <div className="home-offers-inner">
          <h2 id="home-offers-title" className="section-title">
            {customerType === CUSTOMER_PRIVATE ? "Unsere Massagen" : "Unsere Angebote"}
          </h2>
          <div
            className={`home-offer-grid ${
              customerType === CUSTOMER_BUSINESS
                ? "home-offer-grid--business"
                : "home-offer-grid--private"
            }`}
          >
            {offerCards.map((offer) => (
              <article
                key={offer.id}
                className={`home-offer-card home-offer-card--${offer.id} ${
                  offer.featured ? "is-featured" : ""
                }`}
                aria-label={`${offer.title}: Termin auswählen`}
              >
                {offer.id === "wakeup-plus-business" ? (
                  <div className="home-offer-badge-row">
                    <Link
                      to={bookingHrefForOffer(offer.id, customerType, true)}
                      className="home-offer-featured-badge home-offer-featured-badge--action"
                      aria-label={`${monthlyAction.label} buchen`}
                    >
                      {monthlyAction.label}
                    </Link>
                    <span className="home-offer-featured-badge">{offer.spotlight}</span>
                  </div>
                ) : offer.spotlight && (
                  <div className="home-offer-featured-badge">
                    {offer.spotlight}
                  </div>
                )}
                <div className="home-offer-card-content">
                  <h3>{offer.title}</h3>
                  {offer.subtitle && (
                    <p className="home-offer-subtitle">{offer.subtitle}</p>
                  )}
                  {!offer.detailsId && (
                    <p>
                      {offer.highlight && (
                        <strong className="home-offer-highlight">
                          {offer.highlight}
                        </strong>
                      )}
                      {offer.highlight ? " " : ""}
                      {offer.description}
                    </p>
                  )}
                  <div className="home-offer-card-actions">
                    {offer.detailsId && (
                      <button
                        type="button"
                        className="offer-details-trigger"
                        onClick={() => setOpenOfferDetails(offer.detailsId)}
                      >
                        Mehr erfahren
                      </button>
                    )}
                    <Link
                      to={bookingHrefForOffer(offer.id, customerType)}
                      className="home-offer-booking-link"
                    >
                      Termin auswählen
                    </Link>
                  </div>
                </div>
                {offer.badge && <div className="home-offer-badge">{offer.badge}</div>}
                <div className="home-offer-price">
                  {offer.price}
                  {offer.priceNote && <small>{offer.priceNote}</small>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <OfferDetailsModal
        offerId={openOfferDetails}
        onClose={() => setOpenOfferDetails(null)}
      />

      {customerType === CUSTOMER_PRIVATE && (
        <>
          <section className="private-welcome-section">
            <div className="private-welcome-inner">
              <div className="private-welcome-copy fade-in">
              <h2>
              <span className="hero-mobile-hide"></span>D<span className="hero-hoffmann-smallcaps">r</span>. H<span className="hero-hoffmann-smallcaps">offmann</span> Massage
              </h2>
                <p>
                  Das Besondere bei Dr. Hoffmann: Du genießt deine Massage ganz unkompliziert in Alltagskleidung – ohne Vorbereitung. Registrieren, Massage auswählen, Termin buchen und entspannen.
                </p>
                <ol className="private-welcome-steps">
                  <li>
                    <strong>Ort auswählen</strong>
                    <span>
                    Entscheide, wo du deine Massage genießen möchtest: bei uns im Studio oder bei dir vor Ort.
                    </span>
                  </li>
                  <li>
                    <strong>Massage auswählen</strong>
                    <span>
                      Wähle die Massage, die zu dir passt:
                      Wake-up, Classic oder Intense.
                    </span>
                  </li>
                  <li>
                    <strong>Dauer und Wünsche festlegen</strong>
                    <span>
                      Bestimme die gewünschte Dauer und teile uns direkt bei der Buchung mit, ob es besondere Wünsche oder individuelle Bedürfnisse gibt, die wir vorab berücksichtigen sollen.
                    </span>
                  </li>
                  <li>
                    <strong>Termin buchen</strong>
                    <span>
                      Wähle deinen passenden Termin aus und schließe die Buchung ab. Einfach registrieren, auswählen und loslegen.
                    </span>
                  </li>
                </ol>
              </div>
              <aside className="private-trust-card fade-in">
                <h3>Dein Wohlbefinden steht an erster Stelle.</h3>
                <p>
                  <b>Bitte informiere uns</b> vor deiner Massage über aktuelle Verletzungen,
                  Beschwerden, empfindliche Stellen oder gesundheitliche Besonderheiten.
                  So können wir die Anwendung passend gestalten oder gegebenenfalls
                  Bereiche auslassen.
                </p>
              </aside>
            </div>
          </section>

          <section className="private-four-in-one-section">
            <div className="private-four-in-one-inner fade-in">
              <span className="section-kicker">Die 4-in-1-Massage</span>
              <h2 className="section-title">
                Eine Anwendung.<br></br> <em>Vier wohltuende Effekte.</em>
              </h2>
              <p className="section-lead">
                Unsere Massagen fördern die Durchblutung, lösen Verspannungen, beschleunigen die Regeneration und verbessern die Beweglichkeit.
              </p>
              <div className="private-four-video-frame">
                <video
                  ref={massageVideoRef}
                  className="private-four-video"
                  controls
                  playsInline
                  preload="none"
                  poster={dhVideoThumbnail}
                  aria-label="Dr. Hoffmann 4-in-1-Massage Video"
                  onPlay={() => setIsMassageVideoPlaying(true)}
                  onPause={() => setIsMassageVideoPlaying(false)}
                  onEnded={() => setIsMassageVideoPlaying(false)}
                >
                  <source src={dhMassageVideo} type="video/mp4" />
                </video>
                {!isMassageVideoPlaying && (
                  <button
                    type="button"
                    className="private-four-video-play"
                    onClick={handleMassageVideoPlay}
                    aria-label="Massagevideo abspielen"
                  />
                )}
              </div>
            </div>
          </section>

          <section className="private-story-section">
            <div className="private-story-inner">
              <div className="private-story-copy fade-in">
                <span className="section-kicker">Die Geschichte</span>
                <h2 className="section-title">
                  Schnelle Energie - <br></br> immer und überall
                </h2>
                <p>
                  Die Dr. Hoffmann Massage entstand aus der jahrzehntenlangen Erfahrung von Dr. Peter Hoffmann und dem
                  gemeinsamen Austausch mit unserem Personal-Coach Sura über die Möglichkeiten moderner 
                  Perkussionsmassagen. <b>Die Idee dahinter:</b> Massagen sollen unkompliziert, bezahlbar und jederzeit in den Alltag integrierbar sein.
                  So entstand ein Konzept, das neue Energie, Entspannung und Wohlbefinden miteinander verbindet – direkt vor Ort, in Alltagskleidung
                  und ohne großen Zeitaufwand. Ob am Arbeitsplatz, in der Pause oder zwischendurch: Unsere Massagen sollen Menschen die Möglichkeit geben, für
                  ein kleines Budget bewusst abzuschalten, neue Energie zu tanken und anschließend sofort wieder in ihren Alltag zurückzukehren. Neue Energie,
                  wenn sie gebraucht wird – immer und überall.
                </p>
              </div>
              <div className="private-story-image fade-in">
                <img
                  src={suraMassiert1}
                  alt="Sura massiert einen Kunden bei Dr. Hoffmann Massagen"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          <section className="private-faq-section">
            <div className="private-faq-inner">
              <div className="private-faq-heading fade-in">
                <span className="section-kicker">FAQ</span>
                <h2 className="section-title">Häufige Fragen</h2>
              </div>
              <div className="private-faq-list fade-in">
                {PRIVATE_FAQS.map((item, index) => {
                  const isOpen = openFaqIndex === index;
                  const panelId = `private-faq-panel-${index}`;
                  return (
                    <div
                      className={`private-faq-item ${isOpen ? "is-open" : ""}`}
                      key={item.question}
                      ref={(node) => {
                        faqItemRefs.current[index] = node;
                      }}
                    >
                      <button
                        type="button"
                        className="private-faq-question"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => handleFaqToggle(index)}
                      >
                        {item.question}
                      </button>
                      <div
                        id={panelId}
                        className="private-faq-answer"
                        role="region"
                        aria-hidden={!isOpen}
                      >
                        <div className="private-faq-answer-inner">
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      )}

      {customerType === CUSTOMER_BUSINESS && (
        <>
      {/* ─── 5. Target groups ─── */}
      <section className="target-section">
        <div className="target-heading fade-in">
          <span className="section-kicker">Zielgruppen</span>
          <h2 className="section-title">
            Für wen ist das <em>Angebot gemacht</em>?
          </h2>
        </div>
        <div className="target-card-grid">
          {[
            {
              image: spaCosmeticImg,
              title: "Wellness & Kosmetik",
              body: "Optimale Ergänzung für bestehende Studios — neue Kunden, höhere Frequenz.",
            },
            {
              image: fitnessSportImg,
              title: "Fitness & Sport",
              body: "Regeneration für Mitglieder als Zusatz-Service mit klarer Marge.",
            },
            {
              image: unternehmerImg,
              title: "Unternehmer",
              body: "Ideal für Top-Locations, mobile Anbieter, Event- und Gesundheitstage.",
            },
          ].map((c) => (
            <article className="target-card fade-in" key={c.title}>
              <img src={c.image} alt="" loading="lazy" />
              <div className="target-card-copy">
                <h3>{c.title}</h3>
                <p>{c.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ─── 6. Pricing ─── */}
      <section className="section" id="tarife" hidden>
        <div className="fade-in">
          <span className="section-kicker">Unsere Tarife</span>
          <h2 className="section-title">
            Klare Preise — <em>direkt vermarktbar</em>.
          </h2>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card fade-in is-featured">
            <h3>Wake-Up Massage im Studio</h3>
            <div className="pricing-price">
              5 € <small>/ Person.</small>
            </div>
            <p>Ideal für Laufkundschaft und schnellen Durchlauf im Studio oder Center.</p>
            <div style={{ marginTop: "1.4rem" }}>
              <Link to="/buchen" className="btn-primary">Termin im Studio</Link>
            </div>
          </div>
          <div className="pricing-card fade-in">
            <h3>Wake-Up Massage vor Ort</h3>
            <div className="pricing-price">
              5 € <small>zzgl. Anfahrtskosten</small>
            </div>
            <p>Perfekt für Büros, Events, Gesundheitstage und mobile Anbieter mit Premium-Anspruch.</p>
            <div style={{ marginTop: "1.4rem" }}>
              <Link to="/buchen" className="btn-ghost">Mobile Buchung</Link>
            </div>
          </div>
        </div>
        <p
          className="section-lead fade-in"
          style={{ textAlign: "center", margin: "2.2rem auto 0", fontStyle: "italic" }}
        >
          Schnell buchbar. Einfach skalierbar. Sofort vermarktbar.
        </p>
      </section>

      {/* ─── 8. License model ─── */}
      <section className="license-system" id="lizenzmodell">
        <div className="license-system-inner">
          <div className="license-system-copy fade-in">
            <span className="license-kicker">Das Lizenzmodell</span>
            <h2>
              Wir liefern dir das <em>komplette System</em>.
            </h2>
            <p>
              Du konzentrierst dich auf deine Kunden — wir stellen dir die
              Infrastruktur, das Marketing und den Vertrieb zur Verfügung, damit
              du das Angebot schnell und professionell starten kannst.
            </p>
          </div>
          <div className="license-system-map fade-in">
            <h3>Wir übernehmen:</h3>
            <ul>
              <li>Onboarding</li>
              <li>Hybrid-Marketing & Kampagnen</li>
              <li>Buchung & Terminverwaltung</li>
              <li>CRM-System</li>
              <li>Gebietsentwicklung nach Postleitzahl</li>
              <li>Vertriebsunterstützung</li>
            </ul>
          </div>
        </div>
      </section>

        </>
      )}

      <section className="final-cta" id="kontakt">
        {customerType === CUSTOMER_PRIVATE ? (
          <p>Sichere dir jetzt deinen Dr. Hoffmann Massagetermin!</p>
        ) : (
          <p>Starte jetzt mit einem <b>kostenlosen</b> Erstgespräch. Entdecke das Dr. Hoffmann-Prinzip - Schnell. Smart. Skalierbar.</p>
        )}
        <div className="final-cta-buttons">
          <Link to={offersBookingHref} className="btn-primary">
            {customerType === CUSTOMER_PRIVATE ? "Unsere Massagen" : "Unsere Angebote"}
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
